import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FileText,
  ListChecks,
  Camera,
  ScrollText,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  User,
  Users,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMobileData } from '../MobileDataContext'
import { MTopbar } from '../MobileLayout'
import { ScoreRing } from '../../components/ScoreRing'
import { DocumentScanner } from '../scanner/DocumentScanner'
import type { StudentSummary } from '../../api/types'

type Tab = 'rubric' | 'questions' | 'papers'
type ScanPhase = 'select_student' | 'ready' | 'scanning'

export default function MExamSetup() {
  const { examId } = useParams()
  const id = Number(examId)
  const navigate = useNavigate()
  const { t } = useTranslation('pages')
  const { exams, getDetail, getGrades, pagesFor, addPage, removePage, studentsForClass } = useMobileData()

  const [tab, setTab] = useState<Tab>('rubric')

  // Paper upload flow state
  const [scanPhase, setScanPhase] = useState<ScanPhase>('select_student')
  const [currentStudent, setCurrentStudent] = useState<StudentSummary | null>(null)
  const [completedIds, setCompletedIds] = useState<number[]>([])

  const exam = exams.find((e) => e.id === id)
  const detail = getDetail(id)
  const grades = getGrades(id)
  const allStudents = studentsForClass(exam?.class_id)

  const handleSelectStudent = (student: StudentSummary) => {
    setCurrentStudent(student)
    setScanPhase('ready')
  }

  const handleStartScan = () => setScanPhase('scanning')

  const handleScanned = (blob: Blob) => {
    if (!currentStudent) return
    const url = URL.createObjectURL(blob)
    addPage(currentStudent.id, { id: `${Date.now()}`, url })
    // Back to the student hub so the teacher can add another page or finish.
    setScanPhase('ready')
  }

  const handleRemovePage = (pageId: string) => {
    if (!currentStudent) return
    removePage(currentStudent.id, pageId)
  }

  const handleDoneWithStudent = () => {
    if (currentStudent) {
      setCompletedIds((prev) => (prev.includes(currentStudent.id) ? prev : [...prev, currentStudent.id]))
    }
    setCurrentStudent(null)
    setScanPhase('select_student')
  }

  // Full-screen scanner takeover
  if (scanPhase === 'scanning') {
    return (
      <DocumentScanner
        onScanned={handleScanned}
        onClose={() => setScanPhase(currentStudent ? 'ready' : 'select_student')}
      />
    )
  }

  return (
    <>
      <MTopbar title={exam?.session_name ?? t('mobile.examSetup.examFallback')} subtitle={t('mobile.examSetup.subtitle')} back backTo="/m" />

      <div className="m-segment" style={{ borderBottom: '1px solid var(--m-border)' }}>
        <SubTab active={tab === 'rubric'} onClick={() => setTab('rubric')} icon={<ScrollText size={16} />} label={t('mobile.examSetup.tabRubric')} />
        <SubTab active={tab === 'questions'} onClick={() => setTab('questions')} icon={<ListChecks size={16} />} label={t('mobile.examSetup.tabQuestions')} />
        <SubTab active={tab === 'papers'} onClick={() => setTab('papers')} icon={<FileText size={16} />} label={t('mobile.examSetup.tabPapers')} />
      </div>

      <div className="m-scroll">
        <div className="m-page">
          {tab === 'rubric' && <RubricTab detail={detail} />}
          {tab === 'questions' && <QuestionsTab detail={detail} />}
          {tab === 'papers' && (
            <PapersTab
              scanPhase={scanPhase}
              currentStudent={currentStudent}
              completedIds={completedIds}
              allStudents={allStudents}
              grades={grades}
              pagesFor={pagesFor}
              onSelectStudent={handleSelectStudent}
              onStartScan={handleStartScan}
              onRemovePage={handleRemovePage}
              onDoneWithStudent={handleDoneWithStudent}
              onOpenPaper={(paperId) => navigate(`/m/exams/${id}/review/${paperId}`)}
            />
          )}
        </div>
      </div>
    </>
  )
}

function SubTab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button className={`m-segment__btn${active ? ' active' : ''}`} onClick={onClick}>
      {icon} {label}
    </button>
  )
}

/* ── Rubric ──────────────────────────────────────────────── */
function RubricTab({ detail }: { detail?: ReturnType<ReturnType<typeof useMobileData>['getDetail']> }) {
  const { t } = useTranslation('pages')
  const preview = detail?.rubric_preview
  if (!preview) {
    return (
      <div className="m-empty">
        <ScrollText />
        <div>{t('mobile.examSetup.noRubric')}</div>
        <button className="m-btn m-btn--soft">{t('mobile.examSetup.uploadRubric')}</button>
      </div>
    )
  }
  return (
    <div className="m-stack">
      <div className="m-card m-card--pad">
        <div className="m-section-title" style={{ margin: '0 0 8px' }}>{t('mobile.examSetup.rubricSummary')}</div>
        <div style={{ fontSize: '0.92rem', color: 'var(--m-text)' }}>{preview.summary}</div>
      </div>
      <div className="m-section-title">{t('mobile.examSetup.gradingCriteria')}</div>
      <div className="m-stack m-stack--sm">
        {preview.criteria.map((c, i) => (
          <div key={i} className="m-card m-card--pad">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <strong style={{ fontSize: '0.95rem' }}>{c.name}</strong>
              {c.weight_or_points && <span className="m-pill m-pill--neutral">{c.weight_or_points}</span>}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.86rem', color: 'var(--m-text-muted)' }}>{c.expectation}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Questions ───────────────────────────────────────────── */
function QuestionsTab({ detail }: { detail?: ReturnType<ReturnType<typeof useMobileData>['getDetail']> }) {
  const { t } = useTranslation('pages')
  const questions = detail?.questions ?? []
  if (questions.length === 0) {
    return (
      <div className="m-empty">
        <ListChecks />
        <div>{t('mobile.examSetup.noQuestions')}</div>
        <button className="m-btn m-btn--soft">{t('mobile.examSetup.uploadQuestions')}</button>
      </div>
    )
  }
  return (
    <div className="m-stack m-stack--sm">
      {questions.map((q) => {
        const pts = q.maximum_points ?? q.max_points
        return (
          <div key={String(q.question_number)} className="m-card m-card--pad">
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                style={{
                  width: 30, height: 30, flexShrink: 0, display: 'grid', placeItems: 'center',
                  borderRadius: 8, background: 'var(--m-primary-soft)', color: 'var(--m-primary)',
                  fontWeight: 800, fontSize: '0.85rem',
                }}
              >
                {q.question_number}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.9rem' }}>{q.question_text}</div>
                {pts != null && (
                  <div style={{ marginTop: 4, fontSize: '0.8rem', color: 'var(--m-text-muted)' }}>{t('mobile.examSetup.nPoints', { n: pts })}</div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── Papers ──────────────────────────────────────────────── */
function PapersTab({
  scanPhase,
  currentStudent,
  completedIds,
  allStudents,
  grades,
  pagesFor,
  onSelectStudent,
  onStartScan,
  onRemovePage,
  onDoneWithStudent,
  onOpenPaper,
}: {
  scanPhase: ScanPhase
  currentStudent: StudentSummary | null
  completedIds: number[]
  allStudents: StudentSummary[]
  grades: ReturnType<ReturnType<typeof useMobileData>['getGrades']>
  pagesFor: ReturnType<typeof useMobileData>['pagesFor']
  onSelectStudent: (s: StudentSummary) => void
  onStartScan: () => void
  onRemovePage: (pageId: string) => void
  onDoneWithStudent: () => void
  onOpenPaper: (paperId: number) => void
}) {
  const { t } = useTranslation('pages')
  const totalCount = allStudents.length
  const doneCount = completedIds.length
  const allDone = totalCount > 0 && doneCount === totalCount
  const currentPages = currentStudent ? pagesFor(currentStudent.id) : []

  return (
    <div className="m-stack">

      {/* ── Progress bar ── */}
      {totalCount > 0 && (
        <div className="m-upload-progress">
          <div className="m-upload-progress__header">
            <Users size={15} />
            <span>{t('mobile.examSetup.nStudentsDone', { done: doneCount, total: totalCount })}</span>
          </div>
          <div className="m-upload-progress__bar">
            <div
              className="m-upload-progress__fill"
              style={{ width: `${totalCount > 0 ? (doneCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Phase: select student ── */}
      {scanPhase === 'select_student' && (
        <>
          {allDone && (
            <div className="m-upload-done">
              <CheckCircle2 size={36} color="var(--m-success)" />
              <div className="m-upload-done__title">{t('mobile.examSetup.allDoneHeading')}</div>
              <div className="m-upload-done__sub">{t('mobile.examSetup.allDoneBody')}</div>
            </div>
          )}
          {totalCount > 0 && (
            <>
              <div className="m-section-title">{t('mobile.examSetup.studentsSection')}</div>
              <div className="m-stack m-stack--sm">
                {allStudents.map((s) => {
                  const pageCount = pagesFor(s.id).length
                  const done = completedIds.includes(s.id)
                  return (
                    <button key={s.id} className="m-student-row" onClick={() => onSelectStudent(s)}>
                      <div className={`m-student-row__avatar${done ? ' is-done' : ''}`}>
                        {done ? <CheckCircle2 size={16} /> : <User size={16} />}
                      </div>
                      <div className="m-student-row__name">{s.full_name}</div>
                      {pageCount > 0 ? (
                        <span className={`m-pill ${done ? 'm-pill--success' : 'm-pill--neutral'}`}>
                          {t('mobile.examSetup.nPages', { n: pageCount })}
                        </span>
                      ) : (
                        <span className="m-student-row__no">{s.student_no}</span>
                      )}
                      <ChevronRight size={16} color="var(--m-text-faint)" />
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Phase: student hub (scan pages + finish) ── */}
      {scanPhase === 'ready' && currentStudent && (
        <div className="m-upload-ready">
          <div className="m-upload-ready__who">
            <div className="m-upload-ready__avatar"><User size={20} /></div>
            <div>
              <div className="m-upload-ready__name">{currentStudent.full_name}</div>
              <div className="m-upload-ready__no">{currentStudent.student_no}</div>
            </div>
          </div>

          {currentPages.length > 0 ? (
            <div className="m-upload-ready__pages">
              <span>{t('mobile.examSetup.pagesDeleteHint', { n: currentPages.length })}</span>
              <div className="m-upload-ready__thumbs">
                {currentPages.map((p, i) => (
                  <div key={p.id} className="m-upload-thumb">
                    <img src={p.url} alt={t('mobile.examSetup.deletePageAria', { n: i + 1 })} />
                    <span className="m-upload-thumb__num">{i + 1}</span>
                    <button
                      className="m-upload-thumb__del"
                      onClick={() => onRemovePage(p.id)}
                      aria-label={t('mobile.examSetup.deletePageAria', { n: i + 1 })}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="m-upload-ready__hint">
              {t('mobile.examSetup.scanHint')}
            </div>
          )}

          <button className="m-btn m-btn--primary m-btn--block m-btn--lg" onClick={onStartScan}>
            <Camera size={20} />
            {currentPages.length === 0 ? t('mobile.examSetup.scanBtn') : t('mobile.examSetup.addPageBtn')}
          </button>

          {currentPages.length > 0 && (
            <button className="m-btn m-btn--soft m-btn--block" onClick={onDoneWithStudent}>
              <CheckCircle2 size={17} /> {t('mobile.examSetup.nextStudent')}
            </button>
          )}
        </div>
      )}

      {/* ── Graded papers list ── */}
      {grades.length > 0 && (
        <>
          <div className="m-section-title">{t('mobile.examSetup.gradedPapers', { n: grades.length })}</div>
          <div className="m-stack m-stack--sm">
            {grades.map((p) => {
              const total = p.paper_grade?.total_score ?? 0
              return (
                <button key={p.paper_id} className="m-exam-card" onClick={() => onOpenPaper(p.paper_id)}>
                  <ScoreRing score={total} maxScore={100} size={44} />
                  <div className="m-exam-card__body">
                    <div className="m-exam-card__title">{p.student_name}</div>
                    <div className="m-exam-card__meta">
                      <Sparkles size={13} /> {t('mobile.examSetup.gradedMeta', { n: total })}
                    </div>
                  </div>
                  <ChevronRight size={18} color="var(--m-text-faint)" />
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
